import { ReactElement, useCallback, useMemo, useRef } from 'react'
import { useNavigate, useParams } from 'react-router'

import { useBalanceConfig, useBalanceLabels } from '@features/balanceLedger'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { getOrganizationViewUrl } from '../../../../../../utils/routing/getOrganizationViewUrl'
import { SuppliersBalanceEmptyState } from '../../../../elements/SuppliersBalanceEmptyState/SuppliersBalanceEmptyState'
import { useSupplierListFilters } from '../../../../hooks/useSupplierListFilters'
import { SuppliersBalance } from '../../../../types/SuppliersBalance'
import { buildSuppliersBalanceNavQuery } from '../../../../utils/buildSuppliersBalanceNavQuery'
import { SupplierListItem } from './elements/SupplierListItem'
import { SupplierListItemSkeleton } from './elements/SupplierListItemSkeleton'
import { SupplierListSearch } from './elements/SupplierListSearch'
import { useSupplierListKeyboardNav } from './hooks'
import * as Styled from './styles'

const SKELETON_ROW_COUNT = 15

interface Props {
  suppliers: SuppliersBalance[]
  isLoading?: boolean
  period?: string
}

export const SupplierListSidebar = ({ suppliers, isLoading = false, period }: Props): ReactElement => {
  const config = useBalanceConfig()
  const { bt } = useBalanceLabels()
  const [{ searchQuery }] = useSupplierListFilters()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { organization } = useUserOrganization()
  const searchInputRef = useRef<HTMLInputElement>(null)

  const focusSearchInput = useCallback(() => {
    searchInputRef.current?.focus()
  }, [])

  const filteredSuppliers = useMemo(() => {
    const query = typeof searchQuery === 'string' ? searchQuery.toLowerCase() : ''
    if (!query) {
      return suppliers
    }
    return suppliers.filter(
      (s) => s.name.toLowerCase().includes(query) || (s.auxiliaryAccountNo ?? '').toLowerCase().includes(query),
    )
  }, [suppliers, searchQuery])

  const selectedIndex = useMemo(() => {
    const idx = filteredSuppliers.findIndex((s) => s.id === id)
    return idx >= 0 ? idx : 0
  }, [filteredSuppliers, id])

  const navigateToSupplier = useCallback(
    (supplier: SuppliersBalance, { replace }: { replace: boolean }) => {
      if (!organization) {
        return
      }
      const url = getOrganizationViewUrl(
        organization.url,
        config.getDetailRoute(supplier.id),
        buildSuppliersBalanceNavQuery({ period, searchQuery }),
      )
      if (replace) {
        navigate(url, { replace: true })
      } else {
        navigate(url)
      }
    },
    [config, organization, navigate, searchQuery, period],
  )

  const handleClick = useCallback(
    (supplier: SuppliersBalance) => navigateToSupplier(supplier, { replace: false }),
    [navigateToSupplier],
  )

  const { focusedIndex, registerItemRef, handleKeyDown, handleSearchKeyDown } = useSupplierListKeyboardNav({
    suppliers: filteredSuppliers,
    selectedIndex,
    searchQuery,
    isLoading,
    onNavigate: navigateToSupplier,
    onReturnToSearch: focusSearchInput,
  })

  if (isLoading) {
    return (
      <Styled.SidebarWrapper>
        <SupplierListSearch />
        <Styled.SidebarContainer>
          {Array.from({ length: SKELETON_ROW_COUNT }, (_, i) => (
            <Styled.SidebarListItem key={i}>
              <SupplierListItemSkeleton />
            </Styled.SidebarListItem>
          ))}
        </Styled.SidebarContainer>
      </Styled.SidebarWrapper>
    )
  }

  return (
    <Styled.SidebarWrapper>
      <SupplierListSearch ref={searchInputRef} onKeyDown={handleSearchKeyDown} />
      <Styled.SidebarContainer onKeyDown={handleKeyDown}>
        {filteredSuppliers.length === 0 ? (
          <Styled.EmptyState data-testid="supplier-list-empty">
            <SuppliersBalanceEmptyState text={bt('suppliers_list.no_account_found')} />
          </Styled.EmptyState>
        ) : (
          filteredSuppliers.map((supplier, index) => (
            <Styled.SidebarListItem key={supplier.id}>
              <SupplierListItem
                ref={registerItemRef(index)}
                supplier={supplier}
                isSelected={supplier.id === id}
                onClick={handleClick}
                tabIndex={index === focusedIndex ? 0 : -1}
              />
            </Styled.SidebarListItem>
          ))
        )}
      </Styled.SidebarContainer>
    </Styled.SidebarWrapper>
  )
}
